import React from 'react';
import { motion } from 'framer-motion';
import { FaBuilding, FaWarehouse, FaHardHat, FaStar, FaCalendarAlt, FaMapMarkerAlt, FaCheckCircle } from 'react-icons/fa';

const SECTORS = [
  { icon: <FaBuilding />,    label: 'Construction' },
  { icon: <FaWarehouse />,   label: 'Warehouses'   },
  { icon: <FaHardHat />,     label: 'Factories'    },
  { icon: <FaStar />,        label: 'Events'       },
  { icon: <FaCalendarAlt />, label: 'MSMEs'        },
];

const WORKERS = [
  { name: 'Raju M.',    skill: 'Electrician',    city: 'Pune',      exp: '4 yrs', available: true,  delay: 0    },
  { name: 'Suresh K.',  skill: 'Welder',         city: 'Mumbai',    exp: '6 yrs', available: true,  delay: 0.15 },
  { name: 'Priya D.',   skill: 'Supervisor',     city: 'Bengaluru', exp: '3 yrs', available: false, delay: 0.3  },
  { name: 'Arjun T.',   skill: 'Plumber',        city: 'Delhi NCR', exp: '5 yrs', available: true,  delay: 0.45 },
  { name: 'Meena R.',   skill: 'Packer',         city: 'Chennai',   exp: '2 yrs', available: true,  delay: 0.6  },
];

const STATS = [
  { value: '12,000+', label: 'Workers listed' },
  { value: '30+',     label: 'Cities covered' },
  { value: '48 hrs',  label: 'Avg. hire time'  },
];

export default function WorkforceBand({ onStartSearching }) {
  return (
    <section className="py-14 md:py-24 px-4 sm:px-6 bg-orange-50 border-t border-orange-100">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 md:gap-16 items-center">

        {/* Animated worker card panel */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="rounded-3xl border border-orange-200 bg-white shadow-glass-xl
                     p-6 md:order-1 order-2 overflow-hidden"
        >
          {/* Header row */}
          <div className="flex items-center justify-between mb-5">
            <p className="font-display font-black text-sm text-ink-900">Available Workers Near You</p>
            <span className="flex items-center gap-1.5 text-xs font-bold text-brand-green">
              <span className="w-2 h-2 rounded-full bg-brand-green animate-pulse" />
              Live
            </span>
          </div>

          {/* Worker cards */}
          <div className="space-y-3 mb-5">
            {WORKERS.map((w, i) => (
              <motion.div
                key={w.name}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: w.delay, duration: 0.4 }}
                className="flex items-center justify-between px-4 py-3 rounded-2xl
                           bg-orange-50 border border-orange-100"
              >
                <div className="flex items-center gap-3">
                  {/* Avatar placeholder */}
                  <div className="w-8 h-8 rounded-full bg-brand-saffron/20 flex items-center
                                  justify-center text-brand-saffron text-xs font-black shrink-0">
                    {w.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-xs font-black text-ink-900">{w.name}</p>
                    <p className="text-xs text-ink-400">{w.skill} · {w.exp}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-xs text-ink-400">
                    <FaMapMarkerAlt className="text-[10px]" /> {w.city}
                  </span>
                  <FaCheckCircle className={`text-sm ${w.available ? 'text-brand-green' : 'text-gray-300'}`} />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-orange-100">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 + i * 0.1 }}
                className="text-center"
              >
                <p className="font-display font-black text-lg text-brand-saffron">{s.value}</p>
                <p className="text-xs text-ink-400 mt-0.5">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="md:order-2 order-1"
        >
          <span className="text-xs font-black tracking-widest uppercase text-brand-saffron mb-3 block">
            For Businesses & Contractors
          </span>
          <h2 className="font-display font-black text-3xl md:text-4xl lg:text-5xl text-ink-900 leading-tight mb-6">
            Need 50 helpers by Friday? We've got you.
          </h2>
          <p className="text-ink-500 text-lg leading-relaxed mb-8">
            Hire individual workers, skilled teams, or full contract labour — sourced by skill
            and locality. No middle-men. No delays.
          </p>
          <div className="flex flex-wrap gap-3 mb-10">
            {SECTORS.map(s => (
              <span key={s.label}
                    className="flex items-center gap-2 px-4 py-2 rounded-full
                               bg-white border border-orange-200 text-sm font-semibold text-ink-700">
                <span className="text-brand-saffron">{s.icon}</span>
                {s.label}
              </span>
            ))}
          </div>
          <button
            onClick={onStartSearching}
            className="px-8 py-4 rounded-2xl font-black text-sm tracking-wide text-white
                       bg-brand-saffron hover:brightness-110 active:scale-95
                       transition-all shadow-saffron"
          >
            Talk to the workforce team →
          </button>
        </motion.div>

      </div>
    </section>
  );
}
